import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { API_CONFIG } from '@/config/api';
import { AUTH_CONFIG } from '@/config/auth';
import { ApiError } from './errors';
import { readImpersonation } from './impersonationStorage';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - attach Bearer token + impersonation header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(AUTH_CONFIG.accessTokenKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // This tab's practice, not the browser's: another tab opening a practice must not redirect
    // this tab's requests (see impersonationStorage).
    const impersonation = readImpersonation();
    if (impersonation) config.headers['X-Practice-Id'] = impersonation.id;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 refresh
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem(AUTH_CONFIG.refreshTokenKey);
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        // Pass practiceId so the refreshed token preserves the current practice context
        let practiceId: string | undefined;
        try {
          const storedUser = localStorage.getItem(AUTH_CONFIG.userKey);
          if (storedUser) practiceId = JSON.parse(storedUser).practiceId;
        } catch { /* ignore */ }

        const response = await axios.post(`${API_CONFIG.baseURL}/auth/refresh`, {
          refreshToken,
          practiceId,
        });

        const { access_token, refresh_token } = response.data.data;
        localStorage.setItem(AUTH_CONFIG.accessTokenKey, access_token);
        localStorage.setItem(AUTH_CONFIG.refreshTokenKey, refresh_token);

        onRefreshed(access_token);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return apiClient(originalRequest);
      } catch (_refreshError) {
        isRefreshing = false;
        localStorage.removeItem(AUTH_CONFIG.accessTokenKey);
        localStorage.removeItem(AUTH_CONFIG.refreshTokenKey);
        localStorage.removeItem(AUTH_CONFIG.userKey);
        window.location.href = '/login';
        return Promise.reject(_refreshError);
      }
    }

    const message = error.response?.data?.error?.message || error.response?.data?.message || error.message || 'An error occurred';
    const statusCode = error.response?.status || 500;
    const code = error.response?.data?.code;
    const data = error.response?.data;

    return Promise.reject(new ApiError(message, statusCode, code, data));
  }
);

export default apiClient;
